import { useState } from "react";
import { Link, useLocation, useParams } from "wouter";
import { useAuth } from "../../auth/AuthContext";
import { canManageFleet } from "../../auth/roleView";
import { isNotFound, isUnauthorized } from "../../api/errors";
import { useCorpus, useCorpusBinding, useProject } from "../../api/queries";
import type { AgentSession } from "../../api/types";
import { EmptyState, ErrorState, LoadingState } from "../../components/AsyncStates";
import { Breadcrumb } from "../../components/Breadcrumb";
import { Button } from "../../components/Button";
import { PaginatorControls, usePaginator } from "../../components/Paginator";
import { QueryStates } from "../../components/QueryStates";
import { CorpusBindingStatusPill, ProvenanceRungPill } from "../../components/StatePill";
import { StartDevelopDialog } from "../develop/StartDevelopDialog";
import { BindCorpusModal } from "./BindCorpusModal";
import { groupArtifactsByKind } from "./corpusKinds";
import { MarkdownLite } from "./MarkdownLite";
import "./corpus.css";

/** Shortens a git commit sha for display, matching how an operator would read one at a
 * glance (`git log --oneline`'s width) — the full value is still in the `title` attribute
 * for anyone who wants to copy it. Exported for reuse by `ProjectOverviewTab`'s corpus
 * card, which renders the same "Applied &lt;sha&gt;" fact — one implementation, not a
 * second hand-copied one. */
export function shortCommit(sha: string): string {
  return sha.length > 12 ? sha.slice(0, 12) : sha;
}

/** The one-line teaching subtitle for every surface that labels something "Corpus" (UX
 * polish wave 2 decision 2) — a first-time visitor sees this exact sentence wherever the
 * word appears (this page's own header, `ProjectDetailPage`'s Corpus tab panel via
 * `showHeader={false}`, and `ProjectOverviewTab`'s Corpus card), never a second hand-written
 * paraphrase that could drift from it (A-5). */
export const CORPUS_TEACHING_SUBTITLE = "the app's living definition — vision, roadmap, decisions, rules.";

/**
 * The "Corpus" surface (docs/product/ui-information-architecture.md §7): a project's
 * PROJECTED corpus — vision/roadmap/decisions/governance — read only, plus its
 * binding/provenance health. Mounted two ways: standalone at `/projects/:id/corpus`
 * (`App.tsx`, kept for deep links) and as `ProjectDetailPage`'s Corpus TAB, which is the
 * SAME route — "tab and route are the same thing" (IA slice 3 build brief) — so this one
 * component backs both. See `types.ts`'s `CorpusList`/`CorpusBinding` doc comments for the
 * wire contract and `TECHNICAL-CHARTER.md` A-10 for why every state here (unbound / pending
 * / synced) renders exactly what the server said and nothing more.
 */
export function CorpusPage({ showHeader = true }: { showHeader?: boolean } = {}) {
  const { projectId } = useParams<{ projectId: string }>();
  const { session } = useAuth();
  const [, navigate] = useLocation();
  const canManage = canManageFleet(session?.role);
  const [bindModalOpen, setBindModalOpen] = useState(false);
  const [developOpen, setDevelopOpen] = useState(false);

  function handleDevelopLaunched(launched: AgentSession) {
    navigate(`/projects/${projectId}/develop/${launched.id}`);
  }

  const {
    data: project,
    isPending: projectPending,
    isError: projectIsError,
    error: projectError,
    refetch: refetchProject,
  } = useProject(projectId);

  const corpusPaginator = usePaginator(projectId ?? "");
  const {
    data: corpus,
    isPending: corpusPending,
    isError: corpusIsError,
    error: corpusError,
    refetch: refetchCorpus,
    isFetching: corpusFetching,
  } = useCorpus(projectId ?? "", { cursor: corpusPaginator.cursor });

  const {
    data: binding,
    isPending: bindingPending,
    isError: bindingIsError,
    error: bindingError,
    refetch: refetchBinding,
  } = useCorpusBinding(projectId ?? "", { enabled: canManage });

  if (projectPending) {
    return <LoadingState label="Loading project…" />;
  }

  if (projectIsError) {
    if (isUnauthorized(projectError)) {
      return <LoadingState label="Loading project…" />;
    }
    if (isNotFound(projectError)) {
      return (
        <ErrorState
          title="Project not found"
          description="It may have been removed, or the link is out of date. Head back to the projects list."
          action={
            <Link to="/projects" className="button button--secondary">
              Back to Develop
            </Link>
          }
        />
      );
    }
    return (
      <ErrorState
        description={projectError instanceof Error ? projectError.message : "Couldn't load this project."}
        onRetry={refetchProject}
      />
    );
  }

  if (!project) return null;

  // Whether the corpus has actually synced at least once — the ONE signal this page treats
  // as "verified," derived from the server's own `sourceCommit` field, never inferred from
  // the binding alone (A-5/A-10: a binding can exist and still be unsynced).
  const synced = Boolean(corpus?.sourceCommit);

  return (
    <div className="corpus-page">
      {/* `showHeader` is false when this is mounted as `ProjectDetailPage`'s Corpus TAB
          panel — that page already renders the project name and a "back to Develop" link
          in its own shared header, so a second copy here would be a redundant, out-of-
          hierarchy heading (real hierarchy, A-10 §accessibility). The standalone route
          (`/projects/:id/corpus` deep link, and this file's own tests) keeps the full
          header — it has no other title on the page. */}
      {showHeader && (
        <>
          <Breadcrumb
            items={[
              { label: "Develop", to: "/projects" },
              { label: project.name, to: `/projects/${project.id}` },
              { label: "Corpus" },
            ]}
          />

          <h1 className="corpus-page__title">{project.name} corpus</h1>
        </>
      )}
      <p className="corpus-page__subtitle">{CORPUS_TEACHING_SUBTITLE}</p>
      {showHeader && (
        <p className="corpus-page__subtitle">
          The declared intent this project's git repository authors — vision, roadmap,
          decisions, and governance — projected read-only (ADR-0052). A corpus is edited by
          committing to git, never from here.
        </p>
      )}

      {canManage && (
        <section className="corpus-develop" aria-label="Develop with an agent">
          <h2>Develop with an agent</h2>
          <p className="corpus-page__subtitle">
            Co-author this corpus in a conversation instead of editing files by hand: a governed
            session drafts and validates on your own machine, then you review and propose the
            result as a pull request.
          </p>
          <Button variant="primary" onClick={() => setDevelopOpen(true)}>
            Start developing
          </Button>
        </section>
      )}

      {canManage && (
        <section className="corpus-health" aria-label="Corpus health">
          <h2>Corpus health</h2>
          {bindingPending ? (
            <LoadingState label="Loading corpus binding…" />
          ) : bindingIsError ? (
            isNotFound(bindingError) ? (
              <EmptyState
                title="No repository bound yet"
                description="Bind a repository to start syncing this project's vision, roadmap, decisions, and governance from git."
                action={
                  <Button variant="primary" onClick={() => setBindModalOpen(true)}>
                    Bind a repository
                  </Button>
                }
              />
            ) : (
              <ErrorState
                description={
                  bindingError instanceof Error ? bindingError.message : "Couldn't load the corpus binding."
                }
                onRetry={refetchBinding}
              />
            )
          ) : binding ? (
            <div className="corpus-health__card">
              <div className="corpus-health__status">
                <CorpusBindingStatusPill status={synced ? "synced" : "pending"} />
                {synced ? (
                  <ProvenanceRungPill rung={binding.expectedRung} />
                ) : (
                  <span className="corpus-health__requirement">Requires {binding.expectedRung} provenance</span>
                )}
              </div>
              <dl className="corpus-health__meta">
                <div>
                  <dt>Repository</dt>
                  <dd className="corpus-health__mono">{binding.repoRef}</dd>
                </div>
                <div>
                  <dt>Corpus path</dt>
                  <dd className="corpus-health__mono">{binding.corpusPath}</dd>
                </div>
                <div>
                  <dt>Allowed refs</dt>
                  <dd className="corpus-health__mono">{binding.allowedRefs.join(", ")}</dd>
                </div>
                {synced && corpus && (
                  <div>
                    <dt>Synced commit</dt>
                    <dd className="corpus-health__mono" title={corpus.sourceCommit}>
                      {shortCommit(corpus.sourceCommit)}
                    </dd>
                  </div>
                )}
                {/* Last synced: shown ONLY when the server sent a real timestamp. `syncedAt`
                    is absent for a never-synced project (A-10), so we render nothing rather
                    than fabricating a time or a zero date from its absence. */}
                {corpus?.syncedAt && (
                  <div>
                    <dt>Last synced</dt>
                    <dd title={corpus.syncedAt}>{new Date(corpus.syncedAt).toLocaleString()}</dd>
                  </div>
                )}
              </dl>
              <div className="corpus-health__actions">
                <Button variant="secondary" onClick={() => setBindModalOpen(true)}>
                  Edit binding
                </Button>
              </div>
            </div>
          ) : null}
        </section>
      )}

      <section className="corpus-documents" aria-label="Corpus documents">
        <h2>Documents</h2>
        <QueryStates
          isLoading={corpusPending}
          loadingLabel="Loading corpus documents…"
          isError={corpusIsError}
          error={corpusError}
          onRetry={refetchCorpus}
          errorDescription={
            corpusError instanceof Error ? corpusError.message : "Couldn't load this project's corpus."
          }
          isEmpty={Boolean(corpus && corpus.items.length === 0)}
          empty={
            <EmptyState
              title="No corpus documents yet"
              description={
                canManage
                  ? "Bind a repository above to start syncing this project's corpus from git."
                  : "An operator or admin can connect a repository to populate this project's corpus."
              }
            />
          }
        >
          {corpus && (
            <>
              {groupArtifactsByKind(corpus.items).map((group) => (
                <div key={group.kind} className="corpus-kind-group">
                  <h3 className="corpus-kind-group__label">{group.label}</h3>
                  {group.artifacts.map((artifact) => (
                    <article
                      key={`${artifact.kind}-${artifact.slug}`}
                      id={`${artifact.kind}-${artifact.slug}`}
                      className="corpus-doc"
                    >
                      <h4 className="corpus-doc__title">{artifact.title}</h4>
                      <MarkdownLite text={artifact.bodyMd} />
                    </article>
                  ))}
                </div>
              ))}
              <PaginatorControls
                itemCount={corpus.items.length}
                nextCursor={corpus.nextCursor}
                paginator={corpusPaginator}
                noun="documents"
              />
              {corpusFetching && <span className="visually-hidden">Refreshing…</span>}
            </>
          )}
        </QueryStates>
      </section>

      {canManage && (
        <BindCorpusModal
          open={bindModalOpen}
          projectId={project.id}
          existing={binding}
          onClose={() => setBindModalOpen(false)}
        />
      )}

      {canManage && (
        <StartDevelopDialog
          open={developOpen}
          projectId={project.id}
          projectName={project.name}
          onClose={() => setDevelopOpen(false)}
          onLaunched={handleDevelopLaunched}
        />
      )}
    </div>
  );
}
